import { newsList } from "@/mocks/news";
import { plotList } from "@/mocks/plot";
import { castList } from "@/mocks/cast";
import { recommendList } from "@/mocks/recommend";
import type { ContentType } from "@/types/film";

export interface DetailMeta {
  label: string;
  value: string;
}

export interface DetailData {
  id: string;
  type: ContentType;
  categoryLabel: string;
  categoryPath: string;
  title: string;
  subtitle: string;
  image: string;
  meta: DetailMeta[];
  tags: string[];
  body: string;
}

export function getNewsById(id: string) {
  return newsList.find((item) => item.id === id);
}

export function getPlotById(id: string) {
  return plotList.find((item) => item.id === id);
}

export function getCastById(id: string) {
  return castList.find((item) => item.id === id);
}

export function getRecommendById(id: string) {
  return recommendList.find((item) => item.id === id);
}

export function getDetailById(id: string): DetailData | null {
  if (id.startsWith("n")) {
    const item = getNewsById(id);
    if (!item) return null;
    return {
      id: item.id,
      type: "news",
      categoryLabel: "新闻资讯",
      categoryPath: "/news",
      title: item.title,
      subtitle: item.category,
      image: item.image,
      meta: [
        { label: "发布日期", value: item.date },
        { label: "作者", value: item.author },
        { label: "分类", value: item.category },
      ],
      tags: item.tags,
      body: item.content,
    };
  }
  if (id.startsWith("p")) {
    const item = getPlotById(id);
    if (!item) return null;
    return {
      id: item.id,
      type: "plot",
      categoryLabel: "剧情介绍",
      categoryPath: "/plot",
      title: `${item.chapter} · ${item.title}`,
      subtitle: item.act,
      image: item.image,
      meta: [
        { label: "章节", value: item.chapter },
        { label: "所属段落", value: item.act },
        { label: "分类", value: "剧情介绍" },
      ],
      tags: [item.chapter, item.act],
      body: item.content,
    };
  }
  if (id.startsWith("c")) {
    const item = getCastById(id);
    if (!item) return null;
    return {
      id: item.id,
      type: "cast",
      categoryLabel: "演员阵容",
      categoryPath: "/cast",
      title: item.name,
      subtitle: `饰 ${item.character}`,
      image: item.image,
      meta: [
        { label: "饰演", value: item.character },
        { label: "类型", value: item.roleType },
        { label: "代表作品", value: item.works.slice(0, 3).join(" / ") },
      ],
      tags: [item.roleType, item.character],
      body: item.bio,
    };
  }
  if (id.startsWith("r")) {
    const item = getRecommendById(id);
    if (!item) return null;
    return {
      id: item.id,
      type: "recommend",
      categoryLabel: "相关影视推荐",
      categoryPath: "/recommend",
      title: item.title,
      subtitle: `${item.year} · ${item.genre}`,
      image: item.image,
      meta: [
        { label: "上映年份", value: item.year },
        { label: "类型", value: item.genre },
        { label: "评分", value: item.rating },
      ],
      tags: [item.year, item.genre],
      body: item.summary,
    };
  }
  return null;
}

export interface SearchResult {
  id: string;
  type: ContentType;
  typeLabel: string;
  title: string;
  summary: string;
  image: string;
  path: string;
}

const typeLabels: Record<ContentType, string> = {
  news: "新闻资讯",
  plot: "剧情介绍",
  cast: "演员阵容",
  recommend: "影视推荐",
};

const typePath: Record<ContentType, string> = {
  news: "/news",
  plot: "/plot",
  cast: "/cast",
  recommend: "/recommend",
};

export function searchAll(keyword: string, limit?: number): SearchResult[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  const results: SearchResult[] = [];

  newsList.forEach((item) => {
    if (
      item.title.toLowerCase().includes(key) ||
      item.summary.toLowerCase().includes(key) ||
      item.content.toLowerCase().includes(key) ||
      item.tags.some((t) => t.toLowerCase().includes(key))
    ) {
      results.push({
        id: item.id,
        type: "news",
        typeLabel: typeLabels.news,
        title: item.title,
        summary: item.summary,
        image: item.image,
        path: `/detail/${item.id}`,
      });
    }
  });

  plotList.forEach((item) => {
    if (
      item.title.toLowerCase().includes(key) ||
      item.summary.toLowerCase().includes(key) ||
      item.content.toLowerCase().includes(key) ||
      item.chapter.toLowerCase().includes(key)
    ) {
      results.push({
        id: item.id,
        type: "plot",
        typeLabel: typeLabels.plot,
        title: `${item.chapter} · ${item.title}`,
        summary: item.summary,
        image: item.image,
        path: `/detail/${item.id}`,
      });
    }
  });

  castList.forEach((item) => {
    if (
      item.name.toLowerCase().includes(key) ||
      item.character.toLowerCase().includes(key) ||
      item.bio.toLowerCase().includes(key)
    ) {
      results.push({
        id: item.id,
        type: "cast",
        typeLabel: typeLabels.cast,
        title: `${item.name} · 饰 ${item.character}`,
        summary: item.bio,
        image: item.image,
        path: `/detail/${item.id}`,
      });
    }
  });

  recommendList.forEach((item) => {
    if (
      item.title.toLowerCase().includes(key) ||
      item.summary.toLowerCase().includes(key) ||
      item.genre.toLowerCase().includes(key)
    ) {
      results.push({
        id: item.id,
        type: "recommend",
        typeLabel: typeLabels.recommend,
        title: item.title,
        summary: item.summary,
        image: item.image,
        path: `/detail/${item.id}`,
      });
    }
  });

  return typeof limit === "number" ? results.slice(0, limit) : results;
}

export function getRelatedItems(detail: DetailData, limit = 4): SearchResult[] {
  const pool: SearchResult[] = [];

  if (detail.type === "news") {
    newsList.forEach((item) => {
      if (item.id !== detail.id) {
        pool.push({
          id: item.id,
          type: "news",
          typeLabel: typeLabels.news,
          title: item.title,
          summary: item.summary,
          image: item.image,
          path: `/detail/${item.id}`,
        });
      }
    });
  } else if (detail.type === "plot") {
    plotList.forEach((item) => {
      if (item.id !== detail.id) {
        pool.push({
          id: item.id,
          type: "plot",
          typeLabel: typeLabels.plot,
          title: `${item.chapter} · ${item.title}`,
          summary: item.summary,
          image: item.image,
          path: `/detail/${item.id}`,
        });
      }
    });
  } else if (detail.type === "cast") {
    castList.forEach((item) => {
      if (item.id !== detail.id) {
        pool.push({
          id: item.id,
          type: "cast",
          typeLabel: typeLabels.cast,
          title: `${item.name} · 饰 ${item.character}`,
          summary: item.bio,
          image: item.image,
          path: `/detail/${item.id}`,
        });
      }
    });
  } else {
    recommendList.forEach((item) => {
      if (item.id !== detail.id) {
        pool.push({
          id: item.id,
          type: "recommend",
          typeLabel: typeLabels.recommend,
          title: item.title,
          summary: item.summary,
          image: item.image,
          path: `/detail/${item.id}`,
        });
      }
    });
  }

  return pool.slice(0, limit);
}